import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ReadinessBadge from "../components/ReadinessBadge";
import { 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp,
  TrendingDown,
  ArrowRight, 
  Calendar, 
  FileText,
  Loader2,
  Mail,
  Lightbulb,
  Target,
  Info
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuizResultsPage() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailCapturing, setEmailCapturing] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [showEmailForm, setShowEmailForm] = useState(false);

  useEffect(() => {
    fetchResults();
  }, [quizId]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/quiz/results/${quizId}`, {
        headers: getAuthHeaders()
      });
      setResults(response.data);
    } catch (error) {
      toast.error("Failed to load results");
      navigate("/quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCapture = async (e) => {
    e.preventDefault();
    setEmailCapturing(true);
    try {
      const response = await axios.post(`${API}/quiz/capture-email`, {
        name: formData.name,
        email: formData.email,
        quiz_id: quizId
      });
      setResults(prev => ({ ...prev, email_captured: true }));
      setShowEmailForm(false);
      toast.success("Results saved! You can access them anytime from your dashboard.");
    } catch (error) {
      toast.error("Failed to save results");
    } finally {
      setEmailCapturing(false);
    }
  };

  const trackBookingClick = async () => {
    try {
      await axios.post(`${API}/analytics/track`, {
        event_type: "booking_click",
        event_data: { quiz_id: quizId }
      });
    } catch (error) {
      console.error("Failed to track:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
        <p className="text-muted-foreground">Calculating your readiness score...</p>
      </div>
    );
  }

  const percentage = results?.percentage || Math.round((results?.total_score / results?.max_score) * 100);

  return (
    <div className="py-8 md:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Results Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Your Quick Test Results</h1>
          <p className="text-muted-foreground">
            Here's where your business idea stands right now
          </p>
        </div>

        {/* Main Score Badge - Nando's Style */}
        <div className="mb-10" data-testid="results-badge-section">
          <ReadinessBadge 
            level={results?.readiness_level} 
            percentage={percentage}
            size="lg"
          />
          
          {/* Evidence Cap Warning */}
          {results?.evidence_capped && (
            <div className="max-w-md mx-auto mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2 text-sm text-amber-800">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Your score was capped at Amber because you haven't spoken to enough potential customers yet. 
                  An idea nobody has been asked about cannot be Green.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Strengths & Weaknesses Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Strongest Areas */}
          <Card data-testid="strongest-areas-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Your Strongest Areas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {results?.strongest_areas?.map((area, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`strength-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs font-medium text-emerald-600 uppercase">{area.area}</span>
                      <p className="text-sm text-foreground">{area.question}</p>
                      <span className="text-xs text-muted-foreground">Score: {area.score}/4</span>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Weakest Areas with Tips */}
          <Card data-testid="weakest-areas-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingDown className="w-5 h-5 text-red-500" />
                Areas to Improve
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                {results?.weakest_areas?.map((area, index) => (
                  <li key={index} className="space-y-2" data-testid={`weakness-${index}`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-medium text-amber-600 uppercase">{area.area}</span>
                        <p className="text-sm text-foreground">{area.question}</p>
                        <span className="text-xs text-muted-foreground">Score: {area.score}/4</span>
                      </div>
                    </div>
                    {area.tip && (
                      <div className="ml-8 p-2 bg-primary/5 rounded-lg border-l-2 border-primary">
                        <div className="flex items-start gap-2">
                          <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-muted-foreground">{area.tip}</p>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        <Card className="mb-8" data-testid="next-steps-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="w-5 h-5 text-primary" />
              Your Best Next Moves
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {results?.next_steps?.map((step, index) => (
                <li key={index} className="flex items-start gap-3" data-testid={`next-step-${index}`}>
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm pt-0.5">{step}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Save Results (Optional Email Capture) */}
        {!results?.email_captured && !user && (
          <Card className="mb-8 border-dashed" data-testid="save-results-card">
            <CardContent className="p-6">
              {!showEmailForm ? (
                <div className="text-center">
                  <Mail className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-semibold mb-1">Save Your Results</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Enter your email to save these results and access them later
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowEmailForm(true)}
                    className="rounded-full"
                    data-testid="show-email-form-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Save My Results
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleEmailCapture} className="space-y-4 max-w-sm mx-auto">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                      required
                      data-testid="email-capture-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="your@email.com"
                      required
                      data-testid="email-capture-email"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={() => setShowEmailForm(false)}
                      className="flex-1 rounded-full"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 rounded-full" 
                      disabled={emailCapturing}
                      data-testid="email-capture-submit"
                    >
                      {emailCapturing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* CTAs based on readiness level */}
        <div className="space-y-4">
          {/* Primary CTA - Always show Full Assessment */}
          <Card className="border-2 border-primary/20 bg-primary/5" data-testid="cta-assessment">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">Take the Full Assessment</h3>
                    <p className="text-sm text-muted-foreground">
                      Go deeper with our 15-minute assessment. Get your FoundrScore (0-100), 
                      section-by-section analysis, and a one-page business plan.
                    </p>
                  </div>
                </div>
                <Link to="/assessment">
                  <Button className="rounded-full whitespace-nowrap">
                    Start Full Assessment
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Secondary CTAs */}
          <div className="grid md:grid-cols-2 gap-4">
            <a 
              href="https://calendly.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="block"
              onClick={trackBookingClick}
            >
              <Card className="h-full hover:border-secondary/50 transition-colors cursor-pointer" data-testid="cta-book-call">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-secondary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-0.5">Book a Validation Call</h3>
                    <p className="text-xs text-muted-foreground">Get expert guidance on your next steps</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </CardContent>
              </Card>
            </a>

            <Link to="/waiting-list" className="block">
              <Card className="h-full hover:border-primary/50 transition-colors cursor-pointer" data-testid="cta-waitlist">
                <CardContent className="p-5 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-0.5">Join the Waiting List</h3>
                    <p className="text-xs text-muted-foreground">Get early access to new validation tools</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-center text-muted-foreground mt-8 max-w-lg mx-auto">
          This score reflects your answers, not a market guarantee. FoundrScore is guidance, 
          not financial or legal advice. Always validate with real customers before investing.
        </p>
      </div>
    </div>
  );
}
